import { memo } from "react";
import * as styles from "../styles/chat-video-link.module.css";

interface ChatVideoLinkProps {
  href: string;
  children?: React.ReactNode;
}

/**
 * Extract Loom video ID from various Loom URL formats
 */
function getLoomVideoId(url: string): string | null {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === "www.loom.com" || urlObj.hostname === "loom.com") {
      const pathMatch = urlObj.pathname.match(/\/share\/([a-zA-Z0-9]+)/);
      if (pathMatch) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return pathMatch[1];
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Check if the link text indicates this is a video guide link
 */
function isVideoGuideLink(children: React.ReactNode): boolean {
  if (typeof children === "string") {
    const lowerText = children.toLowerCase();
    return (
      lowerText.includes("video guide") ||
      lowerText.includes("watch") ||
      lowerText.includes("video")
    );
  }
  return false;
}

/**
 * Check if URL is external (not app.getredo.com)
 */
function isExternalLink(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return !urlObj.hostname.includes("getredo.com");
  } catch {
    return false;
  }
}

export const ChatVideoLink = memo(function ChatVideoLink({
  href,
  children,
}: ChatVideoLinkProps) {
  const loomId = getLoomVideoId(href);

  // Only embed if it's a Loom link and the link text suggests it's a video
  if (loomId && isVideoGuideLink(children)) {
    return (
      <div className={styles.videoContainer}>
        <a
          className={styles.videoLink}
          href={href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div className={styles.videoWrapper}>
            <iframe
              allowFullScreen
              className={styles.videoIframe}
              src={`https://www.loom.com/embed/${loomId}`}
              title="Video guide"
            />
            <div className={styles.videoOverlay}>
              <div className={styles.videoOverlayText}>
                Click to open in new tab
              </div>
            </div>
          </div>
        </a>
      </div>
    );
  }

  // For navigation links (internal getredo.com links), don't open in new tab
  const isExternal = isExternalLink(href);

  return (
    <a
      href={href}
      rel={isExternal ? "noopener noreferrer" : undefined}
      target={isExternal ? "_blank" : undefined}
    >
      {children}
    </a>
  );
});
